<template>
	<page-template>
		<template slot="content">
			<common-table
				:title="$t('products')"
				showFilter
				:filtered="Boolean(filterDialog.data.text || filterDialog.data.productGroupId)"
				@filter-button-click="filterDialog.show"
				:options.sync="tableOptions"
				:headers="headers"
				:items="products"
				:items-length="pagination.totalItemCount"
				:items-per-page="tableOptions.itemsPerPage"
				:items-per-page-options="tableFooterProps.itemsPerPageOptions"
				:loading="loading"
				:entity-name="$t('product')"
				button-text=" "
				@edit-item="productAction"
				@delete-item="deleteAction"
				@row-click="goToProduct"
			>
				<template #[`item.name`]="{ item }">
					{{ item.name }}
				</template>
				<template #[`item.productGroup`]="{ item }">
					{{ item.productGroupName }}
				</template>
				<template #actions>
					<v-btn color="accent" outlined large elevation="0" @click="dialog.show(null)">
						<v-icon class="icon-plus mr-2" start size="medium"></v-icon>{{ $t("add_item", { item: $t("product") }) }}
					</v-btn>
					<v-btn color="accent" outlined large elevation="0" class="ml-2" @click="importDialog.show()">
						<v-icon class="icon-import mr-2" start size="medium"></v-icon>{{ $t("import_excel") }}
					</v-btn>
				</template>
			</common-table>

			<product-filter-dialog v-model="filterDialog.visibility" :data="filterDialog.data" @done="filterDialog.done" />
			<product-dialog v-model="dialog.visibility" :item="dialog.item" @saved="dialog.saved" @deleted="dialog.deleted" />
			<product-import-dialog
				v-model="importDialog.visibility"
				:templateUrl="templateLink"
				@saved="onImported"
				@error="onImportError"
			/>
		</template>
	</page-template>
</template>

<script>
import uiMixin from "../../../store/interfaces/ui.mixin";
import { pagination as paginationMixin } from "../../mixins/utils.mixin";
import PageTemplate from "../../templates/PageTemplate";
import remote from "../../../data/remote";
import ProductDialog from "./dialog/ProductDialog.vue";
import ProductFilterDialog from "./dialog/ProductFilterDialog.vue";
import CommonTable from "../../partials/CommonTable.vue";
import ProductImportDialog from "./dialog/ProductImportDialog.vue";

export default {
	mixins: [uiMixin, paginationMixin],
	components: { PageTemplate, ProductDialog, ProductFilterDialog, CommonTable, ProductImportDialog },
	data() {
		return {
			products: [],
			loading: true,
			templateLink: process.env.VUE_APP_EXCEL_TEMPLATE_URL,
			filterDialog: {
				visibility: false,
				data: { text: null, productGroupId: null },
				show: () => (this.filterDialog.visibility = true),
				hide: () => (this.filterDialog.visibility = false),
				done: (data) => {
					this.filterDialog.data = data;
					this.$router.push({ query: data });
					this.filterDialog.hide();
					this.loadItems();
				},
			},
			dialog: {
				visibility: false,
				item: null,
				show(p) {
					this.item = p;
					this.visibility = true;
				},
				hide() {
					this.item = null;
					this.visibility = false;
				},
				saved: () => {
					this.dialog.hide();
					this.loadItems();
				},
				deleted: () => {
					this.dialog.hide();
					this.loadItems();
				},
			},
			importDialog: {
				visibility: false,
				show: () => (this.importDialog.visibility = true),
				hide: () => (this.importDialog.visibility = false),
			},
			headers: [
				{ text: this.$t("code"), value: "code" },
				{ text: this.$t("name"), value: "name" },
				{ text: this.$t("product_group"), value: "productGroupName" },
				{ text: this.$t("actions"), value: "actions" },
			],
		};
	},
	methods: {
		productAction(item) {
			this.dialog.show(item);
		},
		deleteAction(item) {
			this.showConfirmDialog({
				message: this.$t("delete_confirm_message"),
				onConfirm: () => {
					this.showProgressDialog();
					remote.products.delete(item.id, {
						onSuccess: () => {
							this.hideProgressDialog();
							this.showSnackBar({ message: this.$t("$message.deleted", { item: this.$t("product") }) });
							this.loadItems();
						},
						onFail: () => {
							this.hideProgressDialog();
							this.showSnackBar({
								message: this.$t("$message.delete_failed", { item: this.$t("product") }),
								color: "error",
							});
						},
					});
				},
			});
		},
		saveProduct(item) {
			this.showProgressDialog();
			const fn = item.id ? "update" : "create";
			remote.products[fn](item, {
				onSuccess: () => {
					this.hideProgressDialog();
					this.dialog.hide();
					this.loadItems();
				},
				onFail: () => this.hideProgressDialog(),
			});
		},
		goToProduct(item) {
			this.$router.push({ name: "product", params: { id: item.id } });
		},
		async loadItems() {
			this.loading = true;
			const { text, productGroupId } = this.filterDialog.data;
			const sortKey = this.tableOptions.sortBy?.[0];
			const sortDesc = this.tableOptions.sortDesc?.[0] ?? false;
			remote.products.listView({
				filter: { text, productGroupId },
				pager: { number: this.tableOptions.page, size: this.tableOptions.itemsPerPage },
				sorter: sortKey ? { property: sortKey, method: sortDesc ? "desc" : "asc" } : undefined,
				lang: this.$lang,
				onSuccess: ({ items, pagination }) => {
					this.products = items;
					this.pagination = pagination;
					this.loading = false;
				},
				onFail: () => {
					this.loading = false;
				},
			});
		},
		onImported(res) {
			const count = Array.isArray(res) ? res.length : 0;
			this.showSnackBar({ message: this.$t("import_success_message", { count }) });
			this.loadItems();
		},
		onImportError(msg) {
			this.showSnackBar({ message: msg, color: "error" });
		},
	},
	created() {
		this.$trackPageView(this.$route.path);
		this.filterDialog.data.text = this.$route.query.text;
		this.filterDialog.data.productGroupId = this.$route.query.productGroupId;
	},
};
</script>

<style scoped>
.custom-add-btn {
	border: 1.6px solid #005d8f;
	color: #005d8f;
}
</style>
